import React, { useState, useRef, useEffect } from 'react';
import { ChevronDown } from 'lucide-react';
import { useTouchDevice } from '@features/onboarding/hooks/useTouchDevice';
import './DropdownFloating.scss';

import { cn } from '@utils/index';

// Option type for dropdown
type Option = string | { label: string; value: string };

// Props for DropdownFloatingSearch
interface DropdownFloatingSearchProps
  extends Omit<React.SelectHTMLAttributes<HTMLSelectElement>, 'onChange'> {
  id: string;
  label: string;
  options: Option[];
  placeholder?: string;
  searchPlaceholder?: string;
  wrapperClass?: string;
  inputClass?: string;
  labelClass?: string;
  chevronClass?: string;
  onChange: (value: string) => void;
  error?: boolean;
}

export const DropdownFloatingSearch = React.forwardRef<
  HTMLSelectElement | HTMLDivElement,
  DropdownFloatingSearchProps
>(
  (
    {
      id,
      label,
      options,
      placeholder = '',
      searchPlaceholder = 'Search...',
      value,
      wrapperClass = '',
      inputClass = '',
      labelClass = '',
      chevronClass = '',
      onChange,
      error,
      disabled,
      ...rest
    },
    ref
  ) => {
    const [open, setOpen] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const dropdownRef = useRef<HTMLDivElement>(null);
    const searchInputRef = useRef<HTMLInputElement>(null);
    const isTouchDevice = useTouchDevice();

     useEffect(() => {
       const closeOnOutsideClick = (e: MouseEvent) => {
         if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
           setOpen(false);
           // Only reset searchTerm if there's a selected value
           if (value) {
             setSearchTerm('');
           }
         }
       };
       document.addEventListener('click', closeOnOutsideClick);
       return () => document.removeEventListener('click', closeOnOutsideClick);
     }, [value]);

    // Focus search input when dropdown opens
    useEffect(() => {
      if (open && searchInputRef.current) {
        searchInputRef.current.focus();
        // Select all text when opening
        searchInputRef.current.select();
      }
    }, [open]);

    const normalizedOptions = options.map((opt) =>
      typeof opt === 'string' ? { label: opt, value: opt } : opt
    );

    // Filter options based on search term
    const filteredOptions = normalizedOptions.filter((opt) =>
      opt.label.toLowerCase().includes(searchTerm.toLowerCase())
    );

    const selectedLabel = normalizedOptions.find((opt) => opt.value === value)?.label;

    const baseWrapperClass = `dropdown-floating ${wrapperClass}`;
    const baseInputClass = `dropdown-floating__field ${inputClass} ${error ? 'dropdown-floating__field_error' : ''}`;
    const baseLabelClass = `dropdown-floating__label ${labelClass}`;

    const [hasValue, setHasValue] = useState(Boolean((value && value.toString().trim().length > 0) || searchTerm.trim().length > 0));

    useEffect(() => {
      setHasValue(Boolean((value && value.toString().trim().length > 0) || searchTerm.trim().length > 0));
    }, [value, searchTerm]);

    if (isTouchDevice) {
      return (
        <div
          className={`${baseWrapperClass} ${hasValue ? 'dropdown-floating_has-value' : ''} ${
            disabled ? 'dropdown-floating_disabled' : ''
          }`}
        >
          <select
            id={id}
            ref={ref as React.Ref<HTMLSelectElement>}
            className={`${baseInputClass} dropdown-floating__field_select ${value ? '': 'text-white'}`}
            value={value || ''}
            onChange={(e) => onChange(e.target.value)}
            disabled={disabled}
            {...rest}
          >
            {placeholder && (
              <option value="" disabled>
                {placeholder}
              </option>
            )}
            {normalizedOptions.map((opt, idx) => (
              <option key={opt.value || `opt-${idx}`} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
          <label htmlFor={id} className={baseLabelClass}>
            {label}
          </label>
          <ChevronDown className={cn('dropdown-floating__chevron', chevronClass)} />
        </div>
      );
    }

    return (
      <div
        className={`${baseWrapperClass} ${hasValue ? 'dropdown-floating_has-value' : ''} ${
          disabled ? 'dropdown-floating_disabled' : ''
        }`}
        ref={dropdownRef}
        tabIndex={-1}
        onBlur={(e) => {
          if (!dropdownRef.current?.contains(e.relatedTarget as Node)) {
            setOpen(false);
            if (!value) {
              setSearchTerm('');
            }
          }
        }}
      >
        <div
          className={`${baseInputClass} dropdown-floating__field_trigger`}
          onClick={() => !disabled && setOpen((prev) => !prev)}
        >
          <input
            ref={searchInputRef}
            data-testid={id}
            type="text"
            placeholder={placeholder}
            value={searchTerm !== '' ? searchTerm : (selectedLabel || '')}
             onChange={(e) => {
               setSearchTerm(e.target.value);
               if (!open) setOpen(true);
               // If input is cleared, also clear the selected value
               if (e.target.value === '') {
                 onChange('');
               }
             }}
            className="dropdown-floating__field-text"
            onClick={(e) => e.stopPropagation()}
             onFocus={() => !disabled && setOpen(true)}
             onKeyDown={(e) => {
               if (e.key === 'Escape') {
                 setOpen(false);
                 setSearchTerm('');
                 onChange('');
               }
             }}
          />
          <ChevronDown
            className={cn(
              'dropdown-floating__chevron',
              chevronClass,
              open && 'dropdown-floating__chevron_open'
            )}
          />
        </div>
        <label htmlFor={id} className={baseLabelClass}>
          {label}
        </label>
        <div
          className={`dropdown-floating__menu ${open ? 'dropdown-floating__menu_open' : ''}`}
        >
          <ul className="dropdown-floating__menu-list">
            {filteredOptions.length > 0 ? (
              filteredOptions.map((opt, idx) => (
                <li
                  key={opt.value || `opt-${idx}`}
                  data-testid={`${id}-option-${opt.value}`}
                  className="dropdown-floating__menu-item"
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => {
                    onChange(opt.value);
                    setOpen(false);
                    // Reset searchTerm after selection
                    setSearchTerm('');
                  }}
                >
                  {opt.label}
                </li>
              ))
            ) : (
              <li className="dropdown-floating__menu-item dropdown-floating__menu-item_no-results">
                No results found
              </li>
            )}
          </ul>
        </div>
      </div>
    );
  }
);

DropdownFloatingSearch.displayName = 'DropdownFloatingSearch';
